import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { 
  TrendingUp, 
  Package, 
  ShoppingCart, 
  Users, 
  Calendar,
  Filter,
  Download,
  ChevronDown,
  ChevronUp,
  RefreshCw
} from 'lucide-react';
import moment from 'moment';

function Analytics() {
  const [loading, setLoading] = useState(true);
  const [statsGenerali, setStatsGenerali] = useState(null);
  const [topProdotti, setTopProdotti] = useState([]);
  const [perFornitore, setPerFornitore] = useState([]);
  const [perCategoria, setPerCategoria] = useState([]);
  const [timeline, setTimeline] = useState([]);
  
  // Filtri
  const [periodo, setPeriodo] = useState('mese');
  const [fornitoreId, setFornitoreId] = useState('');
  const [categoria, setCategoria] = useState('');
  const [dataInizio, setDataInizio] = useState('');
  const [dataFine, setDataFine] = useState('');
  
  // Dati per dropdowns
  const [fornitori, setFornitori] = useState([]);
  const [categorie, setCategorie] = useState([]);
  
  // Espansione sezioni
  const [sezioniAperte, setSezioniAperte] = useState({
    topProdotti: true,
    perFornitore: true,
    perCategoria: true,
    timeline: false
  });

  const periodi = [
    { value: 'settimana', label: 'Questa Settimana' },
    { value: 'settimana-scorsa', label: 'Settimana Scorsa' },
    { value: 'mese', label: 'Questo Mese' },
    { value: 'mese-scorso', label: 'Mese Scorso' },
    { value: 'anno', label: 'Quest\'Anno' },
    { value: 'anno-scorso', label: 'Anno Scorso' },
    { value: 'personalizzato', label: 'Periodo Personalizzato' }
  ];

  useEffect(() => {
    loadFornitori();
    loadCategorie();
  }, []);

  useEffect(() => {
    loadAnalytics();
  }, [periodo, fornitoreId, categoria, dataInizio, dataFine]);

  const loadFornitori = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/fornitori', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setFornitori(response.data.data);
      }
    } catch (error) {
      console.error('Errore caricamento fornitori:', error);
    }
  };

  const loadCategorie = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/fornitori/categorie', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setCategorie(response.data.data);
      }
    } catch (error) {
      console.error('Errore caricamento categorie:', error);
    }
  };

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Costruisci parametri query
      const params = { periodo };
      if (fornitoreId) params.fornitore_id = fornitoreId;
      if (categoria) params.categoria = categoria;
      if (periodo === 'personalizzato') {
        if (!dataInizio || !dataFine) {
          toast.error('Seleziona data inizio e fine');
          setLoading(false);
          return;
        }
        params.data_inizio = dataInizio;
        params.data_fine = dataFine;
      }

      // Chiamate parallele
      const [statsRes, topProdottiRes, perFornitoreRes, perCategoriaRes, timelineRes] = await Promise.all([
        axios.get('/api/analytics/stats', { headers, params }),
        axios.get('/api/analytics/top-prodotti', { headers, params: { ...params, limit: 10 } }),
        axios.get('/api/analytics/per-fornitore', { headers, params }),
        axios.get('/api/analytics/per-categoria', { headers, params }),
        axios.get('/api/analytics/timeline', { headers, params: { ...params, gruppo: 'giorno' } })
      ]);

      setStatsGenerali(statsRes.data.data);
      setTopProdotti(topProdottiRes.data.data);
      setPerFornitore(perFornitoreRes.data.data);
      setPerCategoria(perCategoriaRes.data.data);
      setTimeline(timelineRes.data.data);
    } catch (error) {
      console.error('Errore caricamento analytics:', error);
      toast.error('Errore nel caricamento dei dati analytics');
    } finally {
      setLoading(false);
    }
  };

  const toggleSezione = (sezione) => {
    setSezioniAperte(prev => ({
      ...prev,
      [sezione]: !prev[sezione]
    }));
  };

  const resetFiltri = () => {
    setPeriodo('mese');
    setFornitoreId('');
    setCategoria('');
    setDataInizio('');
    setDataFine('');
  };

  if (loading && !statsGenerali) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics & Statistiche</h1>
          <p className="mt-1 text-sm text-gray-500">
            Analizza le tendenze e le performance degli ordini
          </p>
        </div>
      </div>

      {/* Filtri */}
      <div className="bg-white shadow rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium text-gray-900 flex items-center">
            <Filter className="h-5 w-5 mr-2 text-blue-600" />
            Filtri
          </h2>
          <button
            onClick={resetFiltri}
            className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
          >
            <RefreshCw className="h-4 w-4 mr-1" />
            Reset
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Periodo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Periodo
            </label>
            <select
              value={periodo}
              onChange={(e) => setPeriodo(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {periodi.map(p => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>

          {/* Date personalizzate */}
          {periodo === 'personalizzato' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data Inizio
                </label>
                <input
                  type="date"
                  value={dataInizio}
                  onChange={(e) => setDataInizio(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data Fine
                </label>
                <input
                  type="date"
                  value={dataFine}
                  onChange={(e) => setDataFine(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </>
          )}

          {/* Fornitore */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fornitore
            </label>
            <select
              value={fornitoreId}
              onChange={(e) => setFornitoreId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Tutti i fornitori</option>
              {fornitori.map(f => (
                <option key={f.id} value={f.id}>{f.nome}</option>
              ))}
            </select>
          </div>

          {/* Categoria */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Categoria
            </label>
            <select
              value={categoria}
              onChange={(e) => setCategoria(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Tutte le categorie</option>
              {categorie.map(c => (
                <option key={c} value={c}>{c.replace('_', ' ')}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Statistiche Generali */}
      {statsGenerali && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 overflow-hidden shadow-lg rounded-lg transition-transform hover:scale-105">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="bg-blue-600 p-3 rounded-lg">
                    <ShoppingCart className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-600 truncate">
                      Ordini Totali
                    </dt>
                    <dd className="text-3xl font-bold text-gray-900">
                      {statsGenerali.totale_ordini}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-green-100 overflow-hidden shadow-lg rounded-lg transition-transform hover:scale-105">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="bg-green-600 p-3 rounded-lg">
                    <Package className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-600 truncate">
                      Prodotti Unici
                    </dt>
                    <dd className="text-3xl font-bold text-gray-900">
                      {statsGenerali.prodotti_unici}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-purple-100 overflow-hidden shadow-lg rounded-lg transition-transform hover:scale-105">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="bg-purple-600 p-3 rounded-lg">
                    <TrendingUp className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-600 truncate">
                      Quantità Totale
                    </dt>
                    <dd className="text-3xl font-bold text-gray-900">
                      {statsGenerali.quantita_totale}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-orange-50 to-orange-100 overflow-hidden shadow-lg rounded-lg transition-transform hover:scale-105">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="bg-orange-600 p-3 rounded-lg">
                    <Users className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-600 truncate">
                      Fornitori Attivi
                    </dt>
                    <dd className="text-3xl font-bold text-gray-900">
                      {statsGenerali.fornitori_attivi}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Top Prodotti */}
      <div className="bg-white shadow-lg rounded-lg overflow-hidden transition-all duration-300 hover:shadow-xl">
        <div 
          className="px-6 py-4 border-b border-gray-200 flex items-center justify-between cursor-pointer bg-gradient-to-r from-blue-50 to-transparent hover:from-blue-100 transition-all duration-200"
          onClick={() => toggleSezione('topProdotti')}
        >
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <div className="bg-blue-600 p-2 rounded-lg mr-3">
              <TrendingUp className="h-5 w-5 text-white" />
            </div>
            Top 10 Prodotti Più Ordinati
          </h2>
          {sezioniAperte.topProdotti ? (
            <ChevronUp className="h-5 w-5 text-gray-400 transition-transform" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-400 transition-transform" />
          )}
        </div>
        {sezioniAperte.topProdotti && (
          <div className="px-6 py-4 overflow-x-auto">
            {topProdotti.length === 0 ? (
              <p className="text-center text-gray-500 py-4">Nessun dato disponibile</p>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Prodotto</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipologia</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Categoria</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Quantità Totale</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">N° Ordini</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Media per Ordine</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {topProdotti.map((prodotto, index) => (
                    <tr key={prodotto.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{index + 1}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{prodotto.nome}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{prodotto.tipologia}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        <span className="inline-flex px-2 py-1 text-xs font-medium rounded bg-blue-100 text-blue-800">
                          {prodotto.categoria.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-center font-semibold text-gray-900">{prodotto.quantita_totale}</td>
                      <td className="px-4 py-3 text-sm text-center text-gray-500">{prodotto.numero_ordini}</td>
                      <td className="px-4 py-3 text-sm text-center text-gray-500">{prodotto.quantita_media}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {/* Per Fornitore */}
      <div className="bg-white shadow-lg rounded-lg overflow-hidden transition-all duration-300 hover:shadow-xl">
        <div 
          className="px-6 py-4 border-b border-gray-200 flex items-center justify-between cursor-pointer bg-gradient-to-r from-orange-50 to-transparent hover:from-orange-100 transition-all duration-200"
          onClick={() => toggleSezione('perFornitore')}
        >
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <div className="bg-orange-600 p-2 rounded-lg mr-3">
              <Users className="h-5 w-5 text-white" />
            </div>
            Distribuzione per Fornitore
          </h2>
          {sezioniAperte.perFornitore ? (
            <ChevronUp className="h-5 w-5 text-gray-400 transition-transform" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-400 transition-transform" />
          )}
        </div>
        {sezioniAperte.perFornitore && (
          <div className="px-6 py-4 overflow-x-auto">
            {perFornitore.length === 0 ? (
              <p className="text-center text-gray-500 py-4">Nessun dato disponibile</p>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fornitore</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">N° Ordini</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Prodotti Diversi</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Quantità Totale</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {perFornitore.map((fornitore) => (
                    <tr key={fornitore.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{fornitore.fornitore_nome || 'Sconosciuto'}</td>
                      <td className="px-4 py-3 text-sm text-center text-gray-500">{fornitore.numero_ordini}</td>
                      <td className="px-4 py-3 text-sm text-center text-gray-500">{fornitore.prodotti_diversi}</td>
                      <td className="px-4 py-3 text-sm text-center font-semibold text-gray-900">{fornitore.quantita_totale}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {/* Per Categoria */}
      <div className="bg-white shadow-lg rounded-lg overflow-hidden transition-all duration-300 hover:shadow-xl">
        <div 
          className="px-6 py-4 border-b border-gray-200 flex items-center justify-between cursor-pointer bg-gradient-to-r from-green-50 to-transparent hover:from-green-100 transition-all duration-200"
          onClick={() => toggleSezione('perCategoria')}
        >
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <div className="bg-green-600 p-2 rounded-lg mr-3">
              <Package className="h-5 w-5 text-white" />
            </div>
            Distribuzione per Categoria
          </h2>
          {sezioniAperte.perCategoria ? (
            <ChevronUp className="h-5 w-5 text-gray-400 transition-transform" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-400 transition-transform" />
          )}
        </div>
        {sezioniAperte.perCategoria && (
          <div className="px-6 py-4 overflow-x-auto">
            {perCategoria.length === 0 ? (
              <p className="text-center text-gray-500 py-4">Nessun dato disponibile</p>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Categoria</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Prodotti Diversi</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">N° Ordini</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Quantità Totale</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {perCategoria.map((cat) => (
                    <tr key={cat.categoria} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        <span className="inline-flex px-2 py-1 text-xs font-medium rounded bg-blue-100 text-blue-800">
                          {cat.categoria.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-center text-gray-500">{cat.prodotti_diversi}</td>
                      <td className="px-4 py-3 text-sm text-center text-gray-500">{cat.numero_ordini}</td>
                      <td className="px-4 py-3 text-sm text-center font-semibold text-gray-900">{cat.quantita_totale}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {/* Timeline */}
      <div className="bg-white shadow-lg rounded-lg overflow-hidden transition-all duration-300 hover:shadow-xl">
        <div 
          className="px-6 py-4 border-b border-gray-200 flex items-center justify-between cursor-pointer bg-gradient-to-r from-purple-50 to-transparent hover:from-purple-100 transition-all duration-200"
          onClick={() => toggleSezione('timeline')}
        >
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <div className="bg-purple-600 p-2 rounded-lg mr-3">
              <Calendar className="h-5 w-5 text-white" />
            </div>
            Timeline Ordini
          </h2>
          {sezioniAperte.timeline ? (
            <ChevronUp className="h-5 w-5 text-gray-400 transition-transform" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-400 transition-transform" />
          )}
        </div>
        {sezioniAperte.timeline && (
          <div className="px-6 py-4 overflow-x-auto">
            {timeline.length === 0 ? (
              <p className="text-center text-gray-500 py-4">Nessun dato disponibile</p>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">N° Ordini</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Quantità Totale</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {timeline.map((giorno, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        {moment(giorno.data_ordine).format('DD/MM/YYYY')} 
                        <span className="ml-2 text-xs text-gray-500">
                          ({moment(giorno.data_ordine).format('dddd')})
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-center text-gray-500">{giorno.numero_ordini}</td>
                      <td className="px-4 py-3 text-sm text-center font-semibold text-gray-900">{giorno.quantita_totale}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Analytics;

